import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
    cacheKeys,
    DayIntervalUnit,
    IssueType,
    useCreateMutation,
    useCurrentAccount,
    useTogglePin,
    WorkflowQuestionType,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Divider, Menu } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { DeleteIssueModal } from './deleteIssueModal';
import { useIssueContext } from './issueContext';
import { IssueEditModal } from './issueEditModal';
import { IssueEditRoutineLoader } from './issueEditRoutineLoader';
import { FunctionId } from './issueFormatUtils';
import { IssueReminderModal } from './issueReminderModal';
import { ReassignModal } from './reassignModal';

type IssueMenuProps = {
  issue: any;
  hasReminder: boolean;
  onIssueDelete: () => void;
  onPrint?: () => Promise<void>;
};

export const IssueMenuOld = (props: IssueMenuProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { account } = useCurrentAccount();

  const [menuVisible, setMenuVisible] = useState(false);
  const [menuFired, setMenuFired] = useState(0);
  const [isEditIssueVisible, setIsEditIssueVisible] = useState(false);
  const [isTitleModalVisible, setIsTitleModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isReassignModalVisible, setIsReassignModalVisible] = useState(false);
  const [isReminderModalVisible, setIsReminderModalVisible] = useState(false);
  const isDesktop = () => Platform.OS === 'web';
  const { onRoutineSelected } = useIssueContext()!;

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  const { togglePin } = useTogglePin();

  if (!props.issue) {
    return null;
  }

  const openMenu = () => {
    setMenuVisible(true);
    setMenuFired(Date.now());
  };

  const closeMenu = () => {
    // Add fired check to combat paper menu bug:
    // https://github.com/callstack/react-native-paper/issues/3716
    if (Date.now() - menuFired > 400) {
      setMenuVisible(false);
    }
  };

  const defaultValues: any = {
    alertType: '',
    inputField: '',
    days: 1,
    dayInterval: 1,
    dayIntervalUnit: DayIntervalUnit.Days,
    daysStartDate: moment().format('MM/DD/YYYY'),
    onDate: undefined,
    notes: '',
  };

  const submitReminder = async (input: any) => {
    await createWorkflow(
      { input },
      {
        onSuccess: async () => {
          setIsReminderModalVisible(false);
        },
      }
    );
  };

  const handlePinUnpin = async () => {
    await togglePin(props.issue.id, !props.issue.isPinned);
    closeMenu();
  };
  const onReminderSuccess = () => {
    // Refresh the workflow cache to show the new reminder
    // This will trigger a re-render and show the reminder in the UI
    queryClient.invalidateQueries({ queryKey: cacheKeys('workflow').all });
  };

  const onRoutineEditCanceled = () => {
    setIsEditIssueVisible(false);
    setIsTitleModalVisible(true);
  };

  const onRoutineEditCompleted = () => {
    setIsEditIssueVisible(false);
    setIsTitleModalVisible(true);
  };



  return (
    <View>
      <Menu
        statusBarHeight={0}
        visible={menuVisible}
        onDismiss={closeMenu}
        contentStyle={[
          isDesktop() ? styles.desktopMenu : styles.mobileMenu,
          styles.white,
        ]}
        anchor={
          <TouchableOpacity onPress={openMenu}>
            <MaterialCommunityIcons
              name="dots-vertical"
              color={Colors.primary}
              size={24}
            />
          </TouchableOpacity>
        }
      >
        <Menu.Item
          leadingIcon={'pencil'}
          style={styles.white}
          onPress={() => {
            setIsTitleModalVisible(true);
            closeMenu();
          }}
          title={t('issue.menu.edit')}
        />
        <Menu.Item
          leadingIcon={props.issue.isPinned ? 'pin-off' : 'pin'}
          style={styles.white}
          title={
            props.issue.isPinned
              ? t('issue.menu.unpin')
              : t('issue.menu.pinToAsset')
          }
          onPress={handlePinUnpin}
        />
        <Menu.Item
          leadingIcon="swap-horizontal"
          style={styles.white}
          title={t('issue.menu.reassign')}
          onPress={() => {
            setIsReassignModalVisible(true);
            closeMenu();
          }}
        />
        {!props.hasReminder && (
          <Menu.Item
            leadingIcon="alarm"
            style={styles.white}
            title={t('issue.menu.reminder')}
            onPress={() => {
              setIsReminderModalVisible(true);
              closeMenu();
            }}
          />
        )}
        {Platform.OS === 'web' && (
          <Menu.Item
            onPress={() => {
              props.onPrint && props.onPrint();
              closeMenu();
            }}
            leadingIcon="printer"
            title={t('issue.menu.print')}
          />
        )}
        {(props.issue.createdBy?.id !== FunctionId ||
          props.issue.type === IssueType.Alert) &&
          (() => {
            // Frontend guard: if issue is from a one-time-only routine and the user is not global admin, hide delete
            const isOneTime =
              props.issue?.type === WorkflowQuestionType.Routine &&
              props.issue?.routineVersion?.config?.oneTimeOnly;

            const isGlobalAdmin = account?.isGlobalAdmin?.();
            if (isOneTime && !isGlobalAdmin) return null;

            return (
              <>
                <Divider style={styles.divider} />
                <Menu.Item
                  style={styles.white}
                  titleStyle={styles.delete}
                  onPress={() => {
                    setIsDeleteModalVisible(true);
                    closeMenu();
                  }}
                  title={t('issue.menu.delete')}
                />
              </>
            );
          })()}
      </Menu>
      <IssueEditModal
        issue={props.issue}
        visible={isTitleModalVisible}
        hide={() => setIsTitleModalVisible(false)}
        issueId={props.issue?.id}
        currentTitle={props.issue?.title}
        workspaceId={props.issue?.workspace?.id}
        currentLabels={props.issue?.labels}
        currentAdjustCreationDate={props.issue?.adjustCreationDate}
        currentCreatedAt={props.issue?.createdAt}
        onRoutineEdition={() => {
          setIsEditIssueVisible(true);
        }}
      />
      {
        props.issue.type === WorkflowQuestionType.Routine && 
        isEditIssueVisible && (
          <IssueEditRoutineLoader
            isEditRoutineLoader={isEditIssueVisible}
            issue={props.issue}
            currentTitle={props.issue?.title}
            onRoutineEdition={(props) => {
              setIsEditIssueVisible(false);
              setIsTitleModalVisible(false);
              onRoutineSelected({
                ...props,
                onCanceled: () => onRoutineEditCanceled(),
                onCompleted: () => onRoutineEditCompleted(),
              });
            }}
          />
        )
      }
      <DeleteIssueModal
        visible={isDeleteModalVisible}
        hide={() => setIsDeleteModalVisible(false)}
        issueId={props.issue?.id}
        onDelete={props.onIssueDelete}
      />
      <ReassignModal
        visible={isReassignModalVisible}
        hide={() => setIsReassignModalVisible(false)}
        issue={props.issue}
        presentWebStyle={isDesktop()}
      />
      <IssueReminderModal
        visible={isReminderModalVisible}
        hide={() => setIsReminderModalVisible(false)}
        issue={props.issue}
        defaultValues={defaultValues}
        submitReminder={submitReminder}
        onSuccess={onReminderSuccess}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  white: {
    backgroundColor: Colors.white,
  },
  delete: {
    paddingLeft: 10,
    color: Colors.red,
  },
  mobileMenu: {
    top: 60,
    right: 20,
  },
  desktopMenu: {
    top: 30,
  },
  divider: {
    marginTop: 5,
    marginBottom: 5,
    marginLeft: 20,
    marginRight: 20,
  },
});

const CREATE_WORKFLOW = `mutation createWorkflow($input: CreateWorkflowInput!) {
  createWorkflow(input: $input) {
    id
  }
}`;
